// PrintBarcodesDrawer.tsx
import React, { useRef, useState } from "react";
import { Drawer, Card, Typography, Button, Row, Col, Tag } from "antd";
import ReactBarcode from "react-barcode";
import { CloseCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import ConfirmPrintedItems from "./ConfirmPrintedModal";

const { Text } = Typography;

interface ProductVariant {
  [key: string]: string;
}

export interface BarcodeItem {
  idProduct: number;
  brand: string;
  category: string;
  productBarcode: string;
  productCategory: any;
  productId: number;
  productName: string;
  productSKU: string;
  productSubcategory: any;
  subCategory: string;
  totalPrinted: number;
  totalQuantity: number;
  variants: ProductVariant[];
  productVariantValues: any;
  productGrade?: string;
  productPrice?: number;
  stockLocations?: {
    idLocation: number;
    idProduct: number;
    locationName: string;
    product: number;
    quantity: string;
  }[];
}

interface PrintBarcodesDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedItems: BarcodeItem[];
  onPrint?: (items: BarcodeItem[]) => void;
  onPrintComplete?: () => void; // callback after printing is complete
}

const PrintBarcodesDrawer: React.FC<PrintBarcodesDrawerProps> = ({
  open,
  onClose,
  selectedItems,
  onPrint,
  onPrintComplete,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [confirmPrintedBarcodesModalOpen, setConfirmPrintedBarcodesModalOpen] =
    useState(false);
  const [printQuantities, setPrintQuantities] = useState<
    Record<string, number>
  >({});

  const [selectedPrintOptions, setSelectedPrintOptions] = useState<string[]>([
    "productName",
    "category",
    "variants",
    "grade",
    "price",
  ]);

  // Initialize print quantities when items change
  React.useEffect(() => {
    const initialQuantities: Record<string, number> = {};
    selectedItems.forEach((item) => {
      const maxPrintable = (item.totalQuantity || 0) - (item.totalPrinted || 0);
      initialQuantities[item.productBarcode] = maxPrintable;
    });
    setPrintQuantities(initialQuantities);
  }, [selectedItems]);

  const handleQuantityChange = (
    productBarcode: string,
    value: number,
    maxAllowed: number
  ) => {
    setPrintQuantities((prev) => ({
      ...prev,
      [productBarcode]: Math.min(value, maxAllowed),
    }));
  };

  const renderPrintOptionsCheckboxes = () => {
    const options = [
      { label: "Product Name", value: "productName" },
      { label: "Category & Subcategory", value: "category" },
      { label: "Variants", value: "variants" },
      { label: "Product Grade", value: "grade" },
      { label: "Price", value: "price" },
    ];

    return (
      <div style={{ marginBottom: 20 }}>
        <Text strong>Select data to include on barcode:</Text>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginTop: 8,
          }}
        >
          {options.map((opt) => (
            <label key={opt.value}>
              <input
                type="checkbox"
                value={opt.value}
                checked={selectedPrintOptions.includes(opt.value)}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedPrintOptions((prev) =>
                    e.target.checked
                      ? [...prev, value]
                      : prev.filter((v) => v !== value)
                  );
                }}
              />
              <span style={{ marginLeft: 4 }}>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderProductQuantities = (item: BarcodeItem) => {
    const maxPrintable = (item.totalQuantity || 0) - (item.totalPrinted || 0);

    return (
      <div style={{ marginTop: 10, width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card size="small">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <Text strong>Total Quantity: {item.totalQuantity}</Text>
                <Text strong>Already Printed: {item.totalPrinted}</Text>
                <Text strong type="success">
                  Available to Print: {maxPrintable}
                </Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginLeft: "auto",
                  }}
                >
                  <Text strong>Print Amount:</Text>
                  <input
                    type="number"
                    min="0"
                    max={maxPrintable}
                    value={printQuantities[item.productBarcode] || 0}
                    onChange={(e) =>
                      handleQuantityChange(
                        item.productBarcode,
                        parseInt(e.target.value) || 0,
                        maxPrintable
                      )
                    }
                    style={{ width: "80px" }}
                  />
                  <Text type="secondary">/ {maxPrintable}</Text>
                </div>
              </div>
            </Card>
          </Col>
          {item.stockLocations && item.stockLocations.length > 0 && (
            <Col span={24}>
              <Card size="small" title="Stock by Location">
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {item.stockLocations.map((location) => (
                    <Tag
                      key={location.idLocation}
                      color="blue"
                      style={{
                        padding: "8px 12px",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {location.locationName}:
                      </span>
                      <span>{location.quantity} units</span>
                    </Tag>
                  ))}
                </div>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  // Compute a unique key for each item
  const getItemKey = (item: BarcodeItem, index: number): string => {
    return item.idProduct !== undefined
      ? item.idProduct.toString()
      : `fallback-${index}`;
  };

  // Called after printing completes.
  // Shows a modal with a backdrop, mocks an API call delay of 3 seconds,
  // then closes the modal and the drawer.
  const handlePrintComplete = () => {
    setConfirmPrintedBarcodesModalOpen(true);
  };

  const handleCloseConfirmPrintedBarcodesModal = () => {
    setConfirmPrintedBarcodesModalOpen(false);
    onClose();
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint(selectedItems);
    } else if (printRef.current) {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open("", "_blank", "width=600,height=600");
      if (printWindow) {
        const printHtml = `
          <html>
            <head>
              <title>Print Barcodes</title>
              <style>
                /* Set page size for barcode printing */
                @page {
                  size: 58mm 39mm;
                  margin: 0;
                }
                @media print {
                  html, body {
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    height: 100%;
                    transform: scale(0.99);
                  }
                  .printable-item {
                    width: 58mm !important;
                    height: 39mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    box-sizing: border-box;
                    overflow: hidden;
                    page-break-after: always;
                    page-break-inside: avoid;
                  }
                  /* Override Ant Design Card styles for printing */
                  .ant-card {
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                  }
                }
              </style>
            </head>
            <body>
              ${printContents}
            </body>
          </html>
        `;
        printWindow.document.open();
        // Cast to avoid deprecated signature warning.
        (printWindow.document.write as (s: string) => void)(printHtml);
        printWindow.document.close();

        // Attach the afterprint event to trigger the callback once printing completes.
        printWindow.onafterprint = () => {
          printWindow.close();
          handlePrintComplete();
        };

        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    }
  };

  // Create a flattened array of Card elements based on the quantity field.
  const renderedCards = selectedItems
    .map((item, index) => {
      const copies = printQuantities[item.productBarcode] || 0;

      return Array.from({ length: copies }, (_, copyIndex) => (
        <Card
          key={`${getItemKey(item, index)}-${copyIndex}`}
          style={{
            width: "100%",
            maxWidth: "58mm",
            margin: "5px auto",
          }}
          className="printable-item"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {selectedPrintOptions.includes("productName") && (
              <Text>{item.productName}</Text>
            )}
            {selectedPrintOptions.includes("category") && (
              <Text style={{ fontSize: ".6rem", opacity: 0.7 }}>
                {item.productCategory?.productCategoryName} |{" "}
                {item.productSubcategory?.productCategoryName}
              </Text>
            )}
            {selectedPrintOptions.includes("variants") &&
              item.productVariantValues?.length > 0 && (
                <div
                  style={{
                    fontSize: ".6rem",
                    opacity: 0.7,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {item.productVariantValues
                    .filter(
                      (v: any) =>
                        v?.productVariantValue?.productVariantType?.name &&
                        v?.productVariantValue?.name
                    )
                    .map((variant: any, variantIndex: any) => (
                      <div
                        key={`${variantIndex}-som`}
                        style={{ display: "flex", flexWrap: "wrap" }}
                        dangerouslySetInnerHTML={{
                          __html: `<span style="display:inline;margin:1px;">${variant?.productVariantValue?.productVariantType?.name}: ${variant?.productVariantValue?.name}</span>`,
                        }}
                      ></div>
                    ))}
                </div>
              )}
            {selectedPrintOptions.includes("grade") && item.productGrade && (
              <div
                style={{ display: "flex", flexWrap: "wrap" }}
                dangerouslySetInnerHTML={{
                  __html: `<span style="display:inline;margin:1px;">Product Grade: ${item.productGrade}</span>`,
                }}
              ></div>
            )}
            {selectedPrintOptions.includes("price") && item.productPrice && (
              <Text style={{ fontSize: ".8rem", fontWeight: "bold" }}>
                Price: {item.productPrice.toFixed(2)}
              </Text>
            )}
            <ReactBarcode value={item.productBarcode} height={80} />
          </div>
        </Card>
      ));
    })
    .flat(); // Explicitly flatten the array of arrays

  return (
    <>
      <Drawer
        title="Print Selected Items"
        placement="bottom"
        height="100%"
        onClose={onClose}
        open={open}
      >
        {renderPrintOptionsCheckboxes()}

        <div style={{ marginBottom: 20 }}>
          {selectedItems.map((item, index) => (
            <Card key={getItemKey(item, index)} style={{ marginBottom: 16 }}>
              <Text strong>{item.productName}</Text>
              {renderProductQuantities(item)}
            </Card>
          ))}
        </div>

        <div
          ref={printRef}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {renderedCards}
        </div>

        <div
          style={{
            position: "fixed",
            bottom: "15px",
            left: "40%",
            background: "white",
            zIndex: 5,
            padding: "10px 20px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            borderRadius: "10px",
          }}
        >
          <Button
            icon={<CloseCircleOutlined />}
            style={{ margin: "0 10px" }}
            onClick={onClose}
          />
          <Button
            icon={<PrinterOutlined />}
            style={{ margin: "0 10px" }}
            onClick={handlePrint}
          />
        </div>
      </Drawer>
      <ConfirmPrintedItems
        open={confirmPrintedBarcodesModalOpen}
        onClose={handleCloseConfirmPrintedBarcodesModal}
        products={selectedItems.map((item) => ({
          ...item,
          printAmount: printQuantities[item.productBarcode] || 0,
        }))}
      />
    </>
  );
};

export default PrintBarcodesDrawer;
